import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button, ButtonProps, Popper, Icon, Text, Theme } from '@amzn/storm-ui-v3';
import { calendarAlt } from '@amzn/storm-ui-icons-v3';
import { DateRange, DateRangePicker as StormDateRangePicker, PresetRange } from '@amzn/storm-ui-date-picker-v3';
import { getTimezone } from '@urc-ui/services/app-context';
import { useLocale } from '@urc-ui/hooks/date-fns-locale';
import { useLocalizedDate } from '@urc-ui/hooks/i18n';
import { useDateRangePickerDates, usePresets, useCustomPreset } from './use-date-range-picker-dates';
import { DateRangePickerProps, UsePresetsProps } from './types';
import { HMHAnchor } from '@amzn/help-me-here-client';
import { HMH_CREATE_SUBSCRIPTION_PAGE_ID, STATIC_HMH_GUID } from '@urc-ui/pages/create-subscription/constants';

const CUSTOM_PERIOD_ID = 'CUSTOM';

interface CalendarButtonProps {
  readonly buttonProps: ButtonProps;
  readonly selectedPreset: PresetRange;
}

const CalendarIcon = styled(Icon)`
  margin-inline-end: ${({ theme }: { theme: Theme }) => theme.spacing.small};
`;
CalendarIcon.displayName = 'CalendarIcon';

const TimezoneHelpText = styled(Text)`
  margin-top: ${({ theme }) => theme.spacing.large} !important;
  color: ${({ theme }) => theme.palette.boulder};
  max-width: 220px;
`;

const StyledDiv = styled.div`
  margin-right: ${({ theme }) => theme.spacing.medium};
`;

const HmhWidget = styled(Text)`
  max-width: 220px;
`;

const CalendarButton = (props: CalendarButtonProps) => {
  const localizedDates = useLocalizedDate([{ value: props.selectedPreset.start }, { value: props.selectedPreset.end }]);
  const calendarLabel = props.selectedPreset.period !== CUSTOM_PERIOD_ID ? props.selectedPreset.label : localizedDates.join(' - ');

  return (
    <Button id={props.buttonProps.id} buttonRef={props.buttonProps.buttonRef} onClick={props.buttonProps.onClick}>
      <CalendarIcon type={calendarAlt} data-testid={'calendar-button-icon'} />
      <Text inline={true}>{calendarLabel}</Text>
    </Button>
  );
};

const TimeZoneHelpText = () => {
  const { t: translate } = useTranslation();
  const timezoneHelpText = translate('sspa_rcm_ad_reports_date_picker_timezone_help_content', `Dates are based on the campaign's country`);
  const HMHIngressOptions = {
    ingressFontSize: '12px'
  };

  return (
    <StyledDiv>
      <TimezoneHelpText fontSize='small'>{timezoneHelpText}</TimezoneHelpText>
      <HmhWidget>
        <HMHAnchor
          key={STATIC_HMH_GUID.GLOBAL_TIMEZONE}
          contentId={STATIC_HMH_GUID.GLOBAL_TIMEZONE}
          pageId={HMH_CREATE_SUBSCRIPTION_PAGE_ID}
          options={HMHIngressOptions}
        />
      </HmhWidget>
    </StyledDiv>
  );
};

/**
 * Renders popper with a Storm button as a trigger. Clicking on the button renders a Calendar date range picker with
 * various date range presets for use with offline reports. The popper's button label will update with either the preset
 * label or the selected custom time range.
 * @param props see props for details on inputs
 */
const DateRangePicker = (props: DateRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const togglePopover = () => setIsOpen(prevState => !prevState);

  const { t: translate } = useTranslation();
  const timeZone = getTimezone();
  const locale = useLocale();

  const initialDate = undefined;
  const [startDateUtc, setStartDateUtc] = useState<Date | undefined>(initialDate);

  const { previousMonth, minDateISO, maxDateISO } = useDateRangePickerDates({
    todayUtc: new Date(),
    timeZone,
    minDateUtc: props.minDateUtc,
    startDateUtc: startDateUtc,
    locale,
    reportingWindow: props.reportingWindow
  });

  const onChange = (range?: DateRange, selectedStartDate?: Date) => {
    if (!props.reportingWindow) {
      return;
    }
    setStartDateUtc(selectedStartDate);
  };

  const onSubmit = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>, selected?: PresetRange) => {
    props.onSubmit(event, selected);
    // reset the start date so the max date is also reset accordingly in useDateRangePickerDates
    setStartDateUtc(initialDate);
    togglePopover();
  };

  const onCancel = () => {
    // reset the start date so the max date is also reset accordingly in useDateRangePickerDates
    setStartDateUtc(initialDate);
    togglePopover();
  };

  const optionalDatePickerParams = {
    renderZoneOverride: TimeZoneHelpText
  };

  const anchorRef = useRef<HTMLButtonElement | null>();
  return (
    <Popper
      isOpen={isOpen}
      position='bottom'
      align='start'
      withArrow={false}
      autoFocus={true}
      trigger={
        <CalendarButton
          buttonProps={{ id: props.id, buttonRef: anchorRef, onClick: togglePopover }}
          selectedPreset={props.selectedPreset}
        />
      }
    >
      <StormDateRangePicker
        startDate={props.selectedPreset.start}
        endDate={props.selectedPreset.end}
        selectedPreset={props.selectedPreset}
        minDate={minDateISO}
        maxDate={maxDateISO}
        monthInView={previousMonth}
        locale={locale}
        onChange={onChange}
        onSubmit={onSubmit}
        onCancel={onCancel}
        zone={timeZone}
        presetRanges={props.presetRanges}
        customPeriodId={CUSTOM_PERIOD_ID}
        cancelText={translate('sspa_rcm_ad_reports_cancel', 'Cancel')}
        saveText={translate('sspa_rcm_ad_reports_save', 'Save')}
        orientation='horizontal'
        numberOfMonths={2}
        {...optionalDatePickerParams}
      />
    </Popper>
  );
};

export { DateRangePicker, usePresets, useCustomPreset };
export type { DateRangePickerProps, UsePresetsProps };
